import { AggregatedOrderInfo } from '.';

/**
 * Initialise an aggregated order data with all value to 0
 * @returns an initialized aggregated order
 */
export const initAggregatedOrder = (): AggregatedOrderInfo => ({
  sumOrderAverageSpentTaxExcluded: 0,
  sumOrderAverageSpentTaxIncluded: 0,
  sumOrderCustomersCount: 0,
  sumOrderDiscountAmount: 0,
  sumOrderTotalCancelled: 0,
  sumOrderTotalDiscount: 0,
  sumOrderTotalTaxAmount: 0,
  sumOrderTotalTaxExcluded: 0,
  sumOrderTotalTaxIncluded: 0,
});

/**
 * Get the value of one of the aggregated order props or the default value if nothing is set
 *
 * @param aggregatedOrder the aggregated order to get a value from
 * @param propName the prop name to get the value
 * @param defaultValue the default value to use if the aggregated order is undefined of the prop not set
 * @returns the value if found, otherwise the default value, 0 by default
 */
export const getAggregateOrderValue = (
  aggregatedOrder: AggregatedOrderInfo | undefined,
  propName: keyof AggregatedOrderInfo,
  defaultValue: number = 0,
): number => (!aggregatedOrder ? defaultValue : Number(aggregatedOrder[propName]) || defaultValue);

export const computeAverageSpent = (total: number, customersCounts: number) =>
  customersCounts > 0 ? total / customersCounts : total;

/**
 * Get evolution between selected and reference values
 * Ratio between reference and selected
 *
 * @param selected - actual value from which we want the evolution
 * @param reference - reference value to which the selected value is compared to
 */
export const computeEvolution = (selected: number, reference: number): number => {
  if (reference > 0) {
    return parseFloat(((selected - reference) / reference).toFixed(4));
  }

  return selected > 0 ? 1 : 0;
};

/**
 * Sum 2 aggregated order
 *
 * @param aggregatedOrdersA first aggregated order to sum
 * @param aggregatedOrdersB second aggregated order to sum
 * @returns the summed aggregated order
 */
export const sumAggregatedOrders = (
  aggregatedOrdersA: AggregatedOrderInfo = initAggregatedOrder(),
  aggregatedOrdersB: AggregatedOrderInfo = initAggregatedOrder(),
): AggregatedOrderInfo => ({
  sumOrderAverageSpentTaxExcluded: computeAverageSpent(
    aggregatedOrdersA.sumOrderTotalTaxExcluded + aggregatedOrdersB.sumOrderTotalTaxExcluded,
    aggregatedOrdersA.sumOrderCustomersCount + aggregatedOrdersB.sumOrderCustomersCount,
  ),
  sumOrderAverageSpentTaxIncluded: computeAverageSpent(
    aggregatedOrdersA.sumOrderTotalTaxIncluded + aggregatedOrdersB.sumOrderTotalTaxIncluded,
    aggregatedOrdersA.sumOrderCustomersCount + aggregatedOrdersB.sumOrderCustomersCount,
  ),
  sumOrderCustomersCount:
    aggregatedOrdersA.sumOrderCustomersCount + aggregatedOrdersB.sumOrderCustomersCount,
  sumOrderDiscountAmount:
    aggregatedOrdersA.sumOrderDiscountAmount + aggregatedOrdersB.sumOrderDiscountAmount,
  sumOrderTotalCancelled:
    aggregatedOrdersA.sumOrderTotalCancelled + aggregatedOrdersB.sumOrderTotalCancelled,
  sumOrderTotalDiscount:
    aggregatedOrdersA.sumOrderTotalDiscount + aggregatedOrdersB.sumOrderTotalDiscount,
  sumOrderTotalTaxAmount:
    aggregatedOrdersA.sumOrderTotalTaxAmount + aggregatedOrdersB.sumOrderTotalTaxAmount,
  sumOrderTotalTaxExcluded:
    aggregatedOrdersA.sumOrderTotalTaxExcluded + aggregatedOrdersB.sumOrderTotalTaxExcluded,
  sumOrderTotalTaxIncluded:
    aggregatedOrdersA.sumOrderTotalTaxIncluded + aggregatedOrdersB.sumOrderTotalTaxIncluded,
});

/**
 * Compute the evolution of the selected aggregated orders with the given reference
 *
 * @param aggregatedOrdersA aggregated order to use as the base selection
 * @param aggregatedOrdersB aggregated order to use as reference
 * @returns the aggregated order evolution
 */
export const computeAggregatedOrdersEvolution = (
  selected: AggregatedOrderInfo = initAggregatedOrder(),
  reference: AggregatedOrderInfo = initAggregatedOrder(),
): AggregatedOrderInfo => ({
  sumOrderAverageSpentTaxExcluded: computeEvolution(
    computeAverageSpent(selected.sumOrderTotalTaxExcluded, selected.sumOrderCustomersCount),
    computeAverageSpent(reference.sumOrderTotalTaxExcluded, reference.sumOrderCustomersCount),
  ),
  sumOrderAverageSpentTaxIncluded: computeEvolution(
    computeAverageSpent(selected.sumOrderTotalTaxIncluded, selected.sumOrderCustomersCount),
    computeAverageSpent(reference.sumOrderTotalTaxIncluded, reference.sumOrderCustomersCount),
  ),
  sumOrderCustomersCount: computeEvolution(
    selected.sumOrderCustomersCount,
    reference.sumOrderCustomersCount,
  ),
  sumOrderDiscountAmount: computeEvolution(
    selected.sumOrderDiscountAmount,
    reference.sumOrderDiscountAmount,
  ),
  sumOrderTotalCancelled: computeEvolution(
    selected.sumOrderTotalCancelled,
    reference.sumOrderTotalCancelled,
  ),
  sumOrderTotalDiscount: computeEvolution(
    selected.sumOrderTotalDiscount,
    reference.sumOrderTotalDiscount,
  ),
  sumOrderTotalTaxAmount: computeEvolution(
    selected.sumOrderTotalTaxAmount,
    reference.sumOrderTotalTaxAmount,
  ),
  sumOrderTotalTaxExcluded: computeEvolution(
    selected.sumOrderTotalTaxExcluded,
    reference.sumOrderTotalTaxExcluded,
  ),
  sumOrderTotalTaxIncluded: computeEvolution(
    selected.sumOrderTotalTaxIncluded,
    reference.sumOrderTotalTaxIncluded,
  ),
});
